import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useToast } from '@/hooks/use-toast'

/**
 * Summary of job details included in sync response.
 * Provides project data like totals, addresses, etc.
 */
interface JobDetailsSummary {
  totalAmount: number | null
  completedAt: string | null
  propertyAddress: string | null
  clientName: string | null
  lineItemCount: number
  stored: boolean
}

/**
 * Response type from the Jobber Image Processor Worker's /sync-job-images endpoint.
 * Now includes job details for gallery enrichment.
 * @see /apps/production/jobber-image-processor/src/types.ts
 */
interface SyncJobImagesResponse {
  success: boolean
  message: string
  jobNumber: number | null
  totalPhotosInJob: number
  newImagesFound: number
  processed: number
  failed: number
  skipped: number
  errors: Array<{
    jobId: string
    fileName: string
    error: string
    step: 'download' | 'upload' | 'database' | 'validation' | 'processing'
  }>
  /** Job details summary - project data like totals, addresses, etc. */
  jobDetails?: JobDetailsSummary
}

interface SyncParams {
  jobId: string     // e.g., "gid://jobber/Job/12345"
  franchise: string // "hamilton" or "northyork"
}

const WORKER_URL = import.meta.env.VITE_JOBBER_IMAGE_WORKER_URL

/**
 * Mutation hook to sync new images from Jobber for a specific gallery.
 *
 * Flow:
 * 1. Calls Cloudflare Worker /sync-job-images endpoint
 * 2. Worker fetches job photos from N8N/Jobber API
 * 3. Worker compares with existing images in franchise_image_jobs
 * 4. Worker processes only NEW images (download -> Cloudflare Images -> DB)
 * 5. On success, invalidates gallery image queries to refresh UI
 *
 * @param galleryId - The gallery to sync images for
 * @returns React Query mutation with isPending, mutate, etc.
 */
export function useSyncGalleryImages(galleryId: number) {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: async ({ jobId, franchise }: SyncParams): Promise<SyncJobImagesResponse> => {
      if (!WORKER_URL) {
        throw new Error('VITE_JOBBER_IMAGE_WORKER_URL is not configured')
      }

      const response = await fetch(`${WORKER_URL}/sync-job-images`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          // Note: Worker currently doesn't require auth token
          // Can add Authorization header here if worker is updated to require it
        },
        body: JSON.stringify({ jobId, franchise })
      })

      if (!response.ok) {
        const errorText = await response.text()
        throw new Error(`Sync failed: ${response.status} - ${errorText}`)
      }

      return response.json()
    },

    onSuccess: (data) => {
      // Format job total for display if available
      const jobInfo = data.jobDetails
      const totalStr = jobInfo?.totalAmount != null
        ? ` • Job total: $${jobInfo.totalAmount.toLocaleString()}`
        : ''

      if (data.newImagesFound === 0) {
        toast({
          title: 'Gallery is up to date',
          description: `No new images found in Jobber.${totalStr}`,
        })
      } else if (data.processed > 0) {
        toast({
          title: 'Sync complete',
          description: `Synced ${data.processed} image${data.processed !== 1 ? 's' : ''}.${data.failed > 0 ? ` (${data.failed} failed)` : ''}${totalStr}`,
        })
      } else if (data.failed > 0) {
        toast({
          title: 'Sync completed with errors',
          description: `Found ${data.newImagesFound} new images but ${data.failed} failed to process.${totalStr}`,
          variant: 'destructive',
        })
      }

      // Invalidate gallery images queries to refresh the UI
      queryClient.invalidateQueries({ queryKey: ['gallery-images', galleryId] })
      queryClient.invalidateQueries({ queryKey: ['gallery-all-images', galleryId] })
      // Also invalidate the gallery detail in case image counts changed
      queryClient.invalidateQueries({ queryKey: ['gallery', galleryId] })
      // Also invalidate job details cache if it exists
      queryClient.invalidateQueries({ queryKey: ['jobber-job-details'] })
    },

    onError: (error) => {
      console.error('Gallery image sync failed:', error)
      toast({
        title: 'Sync failed',
        description: error instanceof Error ? error.message : 'Unable to sync images from Jobber.',
        variant: 'destructive',
      })
    }
  })
}

export default useSyncGalleryImages
